import React, { useState, useRef } from 'react'
import { Building2, Phone, Mail, FileText, MapPin, Camera, Save, Loader2 } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useAuthStore } from '../../stores/authStore'
import { toast } from 'react-hot-toast'
import organizadoresService from '../../services/organizadoresService'
import apiClient from '../../api/client'

const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || ''

function logoSrc(url) {
  if (!url) return null
  if (url.startsWith('http')) return url
  return `${API_BASE}/${url.replace(/^\//, '')}`
}

export default function MeusDados() {
  const { organizadorAtivo, loadOrganizadorCompleto } = useOrganizador()
  const { user } = useAuthStore()
  const fileInputRef = useRef(null)

  const isAdmin = user?.permission_level === 'admin' || user?.permission_level === 'super_admin'

  const [endereco, setEndereco] = useState({
    address: organizadorAtivo?.address || '',
    cidade:  organizadorAtivo?.cidade  || '',
    estado:  organizadorAtivo?.estado  || '',
    cep:     organizadorAtivo?.cep     || '',
  })

  // Sincroniza campos de endereço quando contexto atualiza (após save ou troca de org)
  React.useEffect(() => {
    setEndereco({
      address: organizadorAtivo?.address || '',
      cidade:  organizadorAtivo?.cidade  || '',
      estado:  organizadorAtivo?.estado  || '',
      cep:     organizadorAtivo?.cep     || '',
    })
    setLogoPreview(null) // limpa preview local; context já tem a URL nova
  }, [organizadorAtivo?.id, organizadorAtivo?.logo_url, organizadorAtivo?.address])
  const [savingEndereco, setSavingEndereco] = useState(false)
  const [uploadingLogo, setUploadingLogo]   = useState(false)
  const [logoPreview, setLogoPreview]       = useState(null)

  const handleEnderecoSave = async () => {
    if (!organizadorAtivo?.id) return
    setSavingEndereco(true)
    try {
      await apiClient.patch(`/organizadores/${organizadorAtivo.id}/endereco`, endereco)
      await loadOrganizadorCompleto(organizadorAtivo.id)
      toast.success('Endereço atualizado com sucesso')
    } catch {
      toast.error('Erro ao salvar endereço')
    } finally {
      setSavingEndereco(false)
    }
  }

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !organizadorAtivo?.id) return

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!allowed.includes(file.type)) {
      toast.error('Formato não permitido. Use JPG, PNG, GIF, WEBP ou SVG.')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 2 MB.')
      return
    }

    setLogoPreview(URL.createObjectURL(file))
    setUploadingLogo(true)
    try {
      await organizadoresService.uploadLogo(organizadorAtivo.id, file)
      await loadOrganizadorCompleto(organizadorAtivo.id)
      toast.success('Logo atualizada com sucesso')
    } catch {
      toast.error('Erro ao enviar logo')
      setLogoPreview(null)
    } finally {
      setUploadingLogo(false)
    }
  }

  if (!organizadorAtivo) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        Nenhum organizador selecionado.
      </div>
    )
  }

  const logoAtual = logoPreview || logoSrc(organizadorAtivo.logo_url)

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-10">

      {/* Cabeçalho */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building2 size={24} className="text-blue-600" />
          Meus Dados
        </h1>
        <p className="text-sm text-gray-500 mt-1">Informações do seu organizador</p>
      </div>

      {/* Logo */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Camera size={16} className="text-gray-500" />
          Logo
        </h2>
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
            {logoAtual ? (
              <img src={logoAtual} alt="Logo" className="w-full h-full object-contain p-1" />
            ) : (
              <Building2 size={32} className="text-gray-300" />
            )}
          </div>
          <div className="flex-1">
            {isAdmin ? (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  Formatos aceitos: JPG, PNG, GIF, WEBP, SVG. Tamanho máximo: 2 MB.
                </p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
                >
                  {uploadingLogo ? (
                    <><Loader2 size={14} className="animate-spin" /> Enviando...</>
                  ) : (
                    <><Camera size={14} /> Alterar Logo</>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </>
            ) : (
              <p className="text-sm text-gray-400 italic">Somente administradores podem alterar a logo.</p>
            )}
          </div>
        </div>
      </div>

      {/* Dados do Organizador (somente leitura) */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Building2 size={16} className="text-gray-500" />
          Dados Cadastrais
          <span className="ml-auto text-xs text-gray-400 font-normal">Somente leitura</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nome / Razão Social" value={organizadorAtivo.name} icon={<Building2 size={14} />} />
          <Field label="E-mail" value={organizadorAtivo.email} icon={<Mail size={14} />} />
          <Field label="CPF / CNPJ" value={organizadorAtivo.cpf_cnpj} icon={<FileText size={14} />} />
          <Field label="Telefone" value={organizadorAtivo.phone} icon={<Phone size={14} />} />
          {organizadorAtivo.phone2 && (
            <Field label="Telefone 2" value={organizadorAtivo.phone2} icon={<Phone size={14} />} />
          )}
          {organizadorAtivo.plano && (
            <Field label="Plano" value={organizadorAtivo.plano} icon={<FileText size={14} />} />
          )}
        </div>
      </div>

      {/* Endereço (editável — só admin) */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <MapPin size={16} className="text-gray-500" />
          Endereço
          {!isAdmin && (
            <span className="ml-auto text-xs text-gray-400 font-normal">Somente leitura</span>
          )}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Logradouro</label>
            <input
              type="text"
              disabled={!isAdmin}
              value={endereco.address}
              onChange={e => setEndereco({ ...endereco, address: e.target.value })}
              placeholder="Rua, número, complemento"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Cidade</label>
            <input
              type="text"
              disabled={!isAdmin}
              value={endereco.cidade}
              onChange={e => setEndereco({ ...endereco, cidade: e.target.value })}
              placeholder="Cidade"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
              <input
                type="text"
                disabled={!isAdmin}
                value={endereco.estado}
                onChange={e => setEndereco({ ...endereco, estado: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="UF"
                maxLength={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">CEP</label>
              <input
                type="text"
                disabled={!isAdmin}
                value={endereco.cep}
                onChange={e => setEndereco({ ...endereco, cep: e.target.value })}
                placeholder="00000-000"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="mt-5 flex justify-end">
            <button
              onClick={handleEnderecoSave}
              disabled={savingEndereco}
              className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {savingEndereco ? (
                <><Loader2 size={14} className="animate-spin" /> Salvando...</>
              ) : (
                <><Save size={14} /> Salvar Endereço</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ label, value, icon }) {
  return (
    <div>
      <label className="flex items-center gap-1 text-xs font-medium text-gray-500 mb-1">
        {icon} {label}
      </label>
      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 min-h-[36px]">
        {value || <span className="text-gray-400 italic">—</span>}
      </div>
    </div>
  )
}
